/**
 * UIController/InteractionHandler event connections (waypoint add/select/drag, sliders, editor controls).
 *
 * RoutePlotter prototype mixin: methods moved verbatim out of main.js
 * (Phase 1 enabling refactor). Every method runs with `this` bound to the
 * RoutePlotter instance; main.js attaches the group via
 * Object.assign(RoutePlotter.prototype, wiringControllersMixin).
 */
import { getFinishContexts } from './finishContexts.js';
import { PATH_VISIBILITY } from '../config/constants.js';
import { Waypoint } from '../models/Waypoint.js';
import { refreshSwatchPicker } from '../components/SwatchPicker.js';
import { ContextMenu } from '../components/ContextMenu.js';
import { snapToAngle } from '../utils/snapToAngle.js';
import {
  branchInsertIndex, canForkFrom, canRejoinBranch, branchEndInfo,
} from '../utils/routeBranches.js';
import { boundEntryWaypointIds } from '../utils/routeAnchors.js';
import { clampImageCoordinate } from '../utils/imageCoordinates.js';
import { loadBackgroundFile, setBackgroundHidden } from './backgroundLoading.js';
import { refuseWhileExporting } from './exporting.js';
import { returnFocus } from '../components/ModeBanner.js';
import { regionOperation } from './regionLines.js';

/**
 * Reorder waypoints to a new major order, each major carrying its
 * trailing minors — a minor shapes the leg from the major before it, so
 * the pair must move together. Minors before the first major keep their
 * place at the front. Majors missing from newMajorOrder (e.g. a filtered
 * drag-and-drop payload) are appended in their original order rather
 * than dropped. Pure: returns a new array, mutates nothing.
 */
export function reorderWaypointBlocks(waypoints, newMajorOrder) {
  const blocks = new Map(); // major → [major, ...its trailing minors]
  const prefix = [];
  let block = null;
  waypoints.forEach(wp => {
    if (wp.isMajor) {
      block = [wp];
      blocks.set(wp, block);
    } else if (block) {
      block.push(wp);
    } else {
      prefix.push(wp);
    }
  });

  const reordered = [...prefix];
  newMajorOrder.forEach(major => {
    const b = blocks.get(major);
    if (b) {
      reordered.push(...b);
      blocks.delete(major);
    }
  });
  blocks.forEach(b => reordered.push(...b));
  return reordered;
}

/**
 * Resolve the insertion boundary for pointer and semantic waypoint authoring.
 * Semantic commands carry `insertAfterId` (`null` means route start); pointer
 * commands omit it and keep the historical selected-minor-run behavior.
 */
export function resolveWaypointInsertIndex(waypoints, data, selectedWaypoint = null) {
  if (Object.prototype.hasOwnProperty.call(data, 'insertAfterId')) {
    if (data.insertAfterId === null) return 0;
    const explicitIndex = waypoints.findIndex(waypoint => waypoint.id === data.insertAfterId);
    return explicitIndex === -1 ? waypoints.length : explicitIndex + 1;
  }

  if (data.isMajor || !selectedWaypoint) return waypoints.length;
  const selectedIndex = waypoints.indexOf(selectedWaypoint);
  if (selectedIndex === -1) return waypoints.length;
  let insertIndex = selectedIndex + 1;
  while (insertIndex < waypoints.length && !waypoints[insertIndex].isMajor) insertIndex += 1;
  return insertIndex;
}

/**
 * Resolve an area handle against a pointer in screen CSS pixels. Keeping this
 * boundary explicit prevents viewport zoom/pan from mixing canvas and screen
 * coordinate spaces.
 * @param {Object} app - RoutePlotter-compatible orchestrator
 * @param {number} screenX
 * @param {number} screenY
 * @returns {Object|null}
 */
export function findAreaHandleAtScreen(app, screenX, screenY) {
  if (!app.selectedWaypoint || !app.areaEditService) return null;
  const imageToScreen = (x, y) => app.imageToScreen(x, y);
  const hit = app.areaEditService.hitTest(
    app.selectedWaypoint,
    screenX,
    screenY,
    imageToScreen
  );
  return hit
    ? { ...hit, waypoint: app.selectedWaypoint, imageToScreen }
    : null;
}

/**
 * Give document commands one execution owner while allowing toolbar and
 * keyboard emitters to share the same EventBus route.
 * @param {Object} app - RoutePlotter-compatible orchestrator
 */
export function setupDocumentCommands(app) {
  app.eventBus.on('history:undo', () => app.undo());
  app.eventBus.on('history:redo', () => app.redo());
  app.eventBus.on('file:save', () => void app.requestProjectSave());
  app.elements.saveProjectBtn?.addEventListener('click', () => app.eventBus.emit('file:save'));
  app.elements.undoBtn?.addEventListener('click', () => app.eventBus.emit('history:undo'));
  app.elements.redoBtn?.addEventListener('click', () => app.eventBus.emit('history:redo'));
}

export const wiringControllersMixin = {
  
  /**
   * Set up event connections for UI Controller and Interaction Handler
   */
  setupControllerEventConnections() {
    // Background events from UIController
    this.eventBus.on('background:upload', (file) => {
      void loadBackgroundFile(this, file);
    });
    
    this.eventBus.on('background:overlay-change', (value) => {
      this.background.overlay = value;
      this.render();
      this.autoSave();
    });
    
    // Background → Show image, or Include in export → Background image: one
    // setting (UI-08)
    this.eventBus.on('background:hidden-change', (hidden) => {
      setBackgroundHidden(this, hidden);
    });

    this.eventBus.on('background:mode-change', (mode) => {
      this.background.fit = mode;
      this.coordinateTransform.fitMode = mode;
      this.updateImageTransform(this.background.image);
      this.render();
      this.autoSave();
    });
    
    // Animation control events from UIController
    // NOTE: These are command events from UI, NOT the state events emitted by AnimationEngine
    // AnimationEngine emits 'play', 'pause', etc. internally - we listen to those in setupAnimationEngineListeners()
    // These listeners are for UI button clicks, keyboard shortcuts, etc.
    this.eventBus.on('ui:animation:play', () => {
      // Nothing to play: refused with its reason, before anything moves (CROWD-08 part A).
      if (this._admitPlayStart?.() === false) return;
      // If animation is at 100%, restart from beginning
      if (this.animationEngine.getProgress() >= 1.0) {
        this.animationEngine.reset();
      }
      this.animationEngine.play();
    });
    this.eventBus.on('ui:animation:pause', () => this.animationEngine.pause());
    this.eventBus.on('ui:animation:skip-start', () => {
      this.animationEngine.reset();
    });
    this.eventBus.on('ui:animation:skip-end', () => this.animationEngine.seekToProgress(1.0));
    this.eventBus.on('ui:animation:seek', (progress) => this.animationEngine.seekToProgress(progress));
    this.eventBus.on('animation:speed-change', (speed) => {
      // Save current path progress before changing speed
      const currentPathProgress = this.animationEngine.getPathProgress();
      
      this.animationEngine.setSpeed(speed);
      
      // Use unified duration update (accounts for segment speeds)
      if (this.pathPoints && this.pathPoints.length > 0) {
        this.updateAnimationDuration(speed);
        
        // Restore path progress by calculating new timeline position
        // This ensures the animation doesn't jump when speed changes
        if (currentPathProgress > 0 && currentPathProgress < 1) {
          this.animationEngine.seekToPathProgress(currentPathProgress);
        }
      }
      
      // Validate zoom transitions (speed affects segment durations)
      this.validateZoomTransitions();
      
      this.autoSave();
    });
    
    // Hold at end (CROWD-06): only the playback duration grows or shrinks,
    // measured again where it stands, so nothing timed against the base
    // timeline moves and the head stays at the same instant. History follows
    // the gesture, not a timer: one entry when it commits (the pointer let
    // go, or a keyboard step), however long the drag paused on the way.
    this.eventBus.on('animation:hold-at-end-change', ({ holdMs, commit }) => {
      if (this.styles.holdAtEndMs !== holdMs) {
        // Another control's pending entry is its own, kept before this one moves.
        this._flushPendingUndo();
        this.styles.holdAtEndMs = holdMs;
        this.refreshSceneEnd();
        this.queueRender();
        this.autoSave();
      }
      if (commit) this.saveUndoState();
    });

    // Duration with fewer than two route waypoints (CROWD-08 part A): the
    // slider sets the base timeline the crowds release across, and the end is
    // measured again where it stands. With a route the slider sends the route
    // speed instead (`animation:speed-change`). History follows the gesture,
    // as Hold at end's does: one entry when it commits (the pointer let go, or
    // a keyboard step).
    this.eventBus.on('animation:base-duration-change', ({ baseDurationMs, commit }) => {
      if (this.waypoints.length >= 2) return;
      if (this.animationEngine.state.baseDuration !== baseDurationMs) {
        // Another control's pending entry is its own, kept before this one moves.
        this._flushPendingUndo();
        this.animationEngine.state.baseDuration = baseDurationMs;
        this.refreshSceneEnd();
        this.queueRender();
        this.autoSave();
      }
      if (commit) this.saveUndoState();
    });
    
    /**
     * JKL Video Editor Style Playback Controls
     * 
     * J: Reverse playback, speed doubles with each press (-1x, -2x, -4x)
     * K: Play/pause toggle (handled by ui:animation:toggle)
     * L: Forward playback, speed doubles with each press (1x, 2x, 4x)
     * 
     * State tracking: jklDirection tracks current direction (1=forward, -1=reverse, 0=stopped)
     */
    this.jklDirection = 0; // Track JKL state: -1=reverse, 0=stopped, 1=forward
    this.jklSpeed = 1;     // Current JKL speed multiplier (1, 2, 4)
    
    this.eventBus.on('animation:jkl-reverse', () => {
      if (this._admitPlayStart?.() === false) return;
      if (this.jklDirection === -1) {
        // Already reversing: double speed (max 4x)
        this.jklSpeed = Math.min(4, this.jklSpeed * 2);
      } else {
        // Start reverse at 1x
        this.jklDirection = -1;
        this.jklSpeed = 1;
      }
      this.animationEngine.setPlaybackSpeed(-this.jklSpeed);
      if (!this.animationEngine.isPlaying()) {
        this.animationEngine.play();
      }
      console.debug(`⏪ JKL Reverse: ${-this.jklSpeed}x`);
    });
    
    this.eventBus.on('animation:jkl-forward', () => {
      if (this._admitPlayStart?.() === false) return;
      if (this.jklDirection === 1) {
        // Already forward: double speed (max 4x)
        this.jklSpeed = Math.min(4, this.jklSpeed * 2);
      } else {
        // Start forward at 1x
        this.jklDirection = 1;
        this.jklSpeed = 1;
      }
      this.animationEngine.setPlaybackSpeed(this.jklSpeed);
      if (!this.animationEngine.isPlaying()) {
        this.animationEngine.play();
      }
      console.debug(`⏩ JKL Forward: ${this.jklSpeed}x`);
    });
    
    // Reset JKL state when animation is toggled via K or space
    this.eventBus.on('ui:animation:toggle', () => {
      // A pause is never refused; a start with nothing to play is (CROWD-08 part A).
      if (this._admitPlayStart?.() === false) return;
      if (this.animationEngine.isPlaying()) {
        this.animationEngine.pause();
        this.jklDirection = 0;
        this.jklSpeed = 1;
      } else {
        // Resume at normal speed
        this.animationEngine.setPlaybackSpeed(1.0);
        this.jklDirection = 1;
        this.jklSpeed = 1;
        this.animationEngine.play();
      }
    });
    
    /**
     * waypoint:nudge - Move waypoint by fraction of canvas dimension
     * More intuitive than pixel-based movement as it scales with canvas size.
     * When the nudged waypoint is part of a multi-selection, the whole
     * selection moves together by the same canvas-pixel delta.
     */
    this.eventBus.on('waypoint:nudge', (data) => {
      const { waypoint, dxFraction, dyFraction } = data;

      // Calculate pixel movement based on canvas dimensions
      const dx = dxFraction * this.displayWidth;
      const dy = dyFraction * this.displayHeight;

      const movers = this.selectedWaypoints.length > 1 && this.selectedWaypoints.includes(waypoint)
        ? this.selectedWaypoints
        : [waypoint];
      const zoom = this.exportSettings.backgroundZoom / 100;

      for (const wp of movers) {
        // Convert current position to canvas coords, apply offset, convert back
        const currentCanvas = this.imageToCanvas(wp.imgX, wp.imgY);
        const newCanvas = { x: currentCanvas.x + dx, y: currentCanvas.y + dy };
        const newImg = this.canvasToImage(newCanvas.x, newCanvas.y);

        // Update waypoint position (clamped to image bounds unless zoomed
        // out, when it may leave the image as far as load accepts: DEF-03)
        if (zoom < 1) {
          wp.imgX = clampImageCoordinate(newImg.x);
          wp.imgY = clampImageCoordinate(newImg.y);
        } else {
          wp.imgX = Math.max(0, Math.min(1, newImg.x));
          wp.imgY = Math.max(0, Math.min(1, newImg.y));
        }
      }

      // Trigger updates
      this.calculatePath();
      this.render();
      this.saveUndoStateDebounced(); // Groups rapid key repeats into single undo entry
      this.autoSave();
    });
    
    /**
     * waypoint:add - Add a new waypoint from InteractionHandler
     * 
     * Major waypoints: Added at end, become selected
     * Minor waypoints: Inserted after selected waypoint, then after each other
     * 
     * Ordering logic:
     * - Click Major A → selected = A, insert at end
     * - Shift+click Minor 1 → insert after A (index of A + 1)
     * - Shift+click Minor 2 → insert after Minor 1 (find last consecutive minor after A)
     * - Click Major B → selected = B, insert at end
     * 
     * This creates: Major A → Minor 1 → Minor 2 → Major B
     * 
     * Note: Bounds checking is done in InteractionHandler before emitting this event
     */
    this.eventBus.on('waypoint:add', (data) => {
      let addX = data.imgX;
      let addY = data.imgY;
      
      // Determine insertion index first (needed for angle snap reference)
      const insertIndex = resolveWaypointInsertIndex(this.waypoints, data, this.selectedWaypoint);
      
      // 15° angle snapping when Shift is held
      if (data.shiftKey && insertIndex > 0) {
        const ref = this.waypoints[insertIndex - 1];
        const snapped = snapToAngle(ref.imgX, ref.imgY, addX, addY);
        addX = snapped.x;
        addY = snapped.y;
      }
      // Zoomed out, a click on the canvas margin lands off the image; keep it
      // within what load accepts (DEF-03)
      addX = clampImageCoordinate(addX);
      addY = clampImageCoordinate(addY);

      const waypoint = data.isMajor ?
        Waypoint.createMajor(addX, addY) : 
        Waypoint.createMinor(addX, addY);
      
      // Copy properties from appropriate source waypoint
      const sourceWaypoint = insertIndex > 0 
        ? this.waypoints[insertIndex - 1] 
        : (this.waypoints.length > 0 ? this.waypoints[0] : null);
      
      if (sourceWaypoint) {
        waypoint.copyPropertiesFrom(sourceWaypoint);
      }
      
      // Insert waypoint at calculated position
      this.waypoints.splice(insertIndex, 0, waypoint);
      this._addWaypointToMap(waypoint);
      
      // Major waypoints become selected (so subsequent minor waypoints follow them)
      if (data.isMajor) {
        this.selectedWaypoint = waypoint;
        this.selectedWaypoints = [waypoint];
        this.uiController?.setSelection([waypoint], waypoint);
        if (this.interactionHandler?.setSelection) {
          this.interactionHandler.setSelection([waypoint], waypoint);
        } else {
          this.interactionHandler?.setSelectedWaypoint?.(waypoint);
        }
      }

      this.eventBus.emit('waypoint:added', waypoint);
    });
    
    // ========== BRANCH AUTHORING (ROUTE-01c) ==========

    /**
     * route:branch-arm — Alt+click landed on a waypoint. Arm the gesture and
     * say so; the next plain canvas click places the branch's first waypoint.
     * Its banner shows in the slot below the header, with a Cancel, so a
     * pointer leaves it as Escape does (UI-06 PR 6, item 2): another mode's
     * banner there ends that mode first, before the gesture is armed. Focus
     * stays where it is: the gesture is on the canvas.
     */
    this.eventBus.on('route:branch-arm', ({ waypoint }) => {
      const verdict = canForkFrom(waypoint);
      if (!verdict.ok) {
        this.eventBus.emit('ui:toast', { message: verdict.reason });
        return;
      }
      const label = waypoint.name || 'this waypoint';
      const owner = this._branchBannerOwner ??= { onReplaced: () => this.eventBus.emit('route:branch-cancel') };
      // Armed again from another waypoint with focus in the banner: where
      // focus goes back to is still where it was before the gesture.
      const active = document.activeElement;
      const focusAtArm = this.modeBanner?.holdsFocus(owner) ? this._branchGesture?.focusAtArm ?? null : active;
      const cancel = () => this.eventBus.emit('route:branch-cancel');
      const shown = this.modeBanner?.show(owner, {
        id: 'branch-banner',
        title: `Branch from ${label}`,
        text: [' — click where it should go. ', { kbd: 'Esc' }, ' to cancel.'],
        actions: [{ label: 'Cancel', className: 'banner-cancel', onClick: cancel }],
      });
      // Refused (claimed inside a replacement's exits, past the slot's
      // bound): no gesture is armed (Codex r2 on 753f93f, 6).
      if (this.modeBanner && !shown) return;
      this.interactionHandler.branchArmed = waypoint;
      this._branchGesture = { forkId: waypoint.id, focusAtArm, owner };
      // Said once (UI-06 J-05): the banner is not a live region.
      this.announce(`Branch from ${label} — click where it should go. Escape to cancel.`);
    });

    // The one cancel path: Escape, an off-image click, the banner's Cancel,
    // another mode's banner, and a fork that has left the model
    // (`followBranchFork`), whose focus waits for the list it rebuilds.
    this.eventBus.on('route:branch-cancel', ({ deferFocus = false } = {}) => {
      if (!this.interactionHandler.branchArmed) return;
      this._endBranchGesture({ deferFocus });
      this.eventBus.emit('ui:toast', { message: 'Branch cancelled' });
    });
    // Opening a project or clearing it ends the gesture: its fork is gone (UI-06 PR 6).
    this.eventBus.on('project:replaced', () => this.followBranchFork());
    this.eventBus.on('app:cleared', () => this.followBranchFork());

    /**
     * route:branch-place — Place the branch's first waypoint.
     *
     * The branch run is inserted after the fork's own leg block so the array
     * still reads in route order (branchInsertIndex). A branch waypoint is a
     * major: it carries timing of its own, which is what makes the branch
     * animate rather than merely bend a leg.
     */
    this.eventBus.on('route:branch-place', ({ imgX, imgY }) => {
      const fork = this.interactionHandler.branchArmed;
      this._endBranchGesture();
      // The fork is the model's own object, as `followBranchFork` asks (Codex r1 on 7a74755, 8)
      if (!fork || this.waypointsById?.get?.(fork.id) !== fork) {
        this.eventBus.emit('ui:toast', { message: 'That waypoint is no longer on the route' });
        return;
      }

      // Zoomed out, a branch can start in the canvas margin, off the image;
      // keep it within what load accepts (DEF-03)
      const waypoint = Waypoint.createMajor(clampImageCoordinate(imgX), clampImageCoordinate(imgY));
      waypoint.copyPropertiesFrom(fork);
      waypoint.branchId = `br_${waypoint.id}`;
      waypoint.branchFrom = fork.id;

      this.waypoints.splice(branchInsertIndex(this.waypoints, fork.id), 0, waypoint);
      this._addWaypointToMap(waypoint);
      this._majorWaypointsCache = null;
      // Snapshot AFTER the mutation: the stack holds post-action states, and
      // undo() pops the current one to restore the previous.
      this.saveUndoState();

      this.selectedWaypoint = waypoint;
      this.selectedWaypoints = [waypoint];
      this.uiController?.setSelection([waypoint], waypoint);
      this.interactionHandler?.setSelection?.([waypoint], waypoint);

      this.calculatePath();
      this.updateAnimationDuration();
      this.updateWaypointList();
      this.eventBus.emit('waypoint:added', waypoint);
      this.autoSave();
      this.queueRender();
      this.announce(`Branch added from ${fork.name || 'the waypoint'}.`);
    });

    /**
     * route:branch-rejoin — A branch's last waypoint was dropped on another
     * waypoint. Dropping on the current target clears the rejoin, so the same
     * gesture both makes and unmakes it; there is no separate "end here"
     * control to find.
     */
    this.eventBus.on('route:branch-rejoin', ({ waypoint, targetId }) => {
      const info = branchEndInfo(this.waypoints, waypoint);
      if (!info || !info.isEnd) return;

      const current = waypoint.branchRejoin || null;
      const next = current === targetId ? null : targetId;
      const verdict = canRejoinBranch(this.waypoints, info.branchId, next);
      if (!verdict.ok) {
        // The drop put the branch's end back where it was: its route is
        // rebuilt for it, or the drag's own path would time a route the
        // project no longer has (DEF-06).
        this.calculatePath();
        this.eventBus.emit('ui:toast', { message: verdict.reason });
        return;
      }

      waypoint.branchRejoin = next;
      this.saveUndoState(); // after the mutation, per the undo-stack contract
      this.calculatePath();
      // Timed now, and the rebuild the path queued with it dropped: a save
      // would otherwise run it again (DEF-06).
      this.invalidateAnimationTiming();
      this.updateWaypointList();
      this.autoSave();
      this.queueRender();

      const targetName = next
        ? (this.waypointsById.get(next)?.name || 'that waypoint')
        : null;
      const message = next ? `Branch rejoins at ${targetName}` : 'Branch now ends here';
      this.eventBus.emit('ui:toast', { message });
    });

    // Note: waypoint:position-changed is handled in _setupEventBusListeners()
    // which applies position, recalculates path, and manages undo saves.
    
    this.eventBus.on('waypoint:selected', (waypoint) => {
      this.selectedWaypoint = waypoint;
      this.selectedWaypoints = [waypoint];
      if (this.interactionHandler?.setSelection) {
        this.interactionHandler.setSelection([waypoint], waypoint);
      } else {
        this.interactionHandler?.setSelectedWaypoint?.(waypoint);
      }
      this.uiController?.setSelection([waypoint], waypoint);
      this.uiController?.updateWaypointEditor(waypoint);
      this.updateWaypointList();
      this.updateWaypointEditor(); // Update RoutePlotter's editor (includes camera controls)
      this._updateCameraControlsVisibility(false); // Single selection mode

      // Sync swatch picker radios with updated hidden input values
      // Without this, clicking a swatch already checked from a previous waypoint
      // won't fire a change event (radio state out of sync with hidden input)
      refreshSwatchPicker('#dot-color');
      refreshSwatchPicker('#segment-color');
      refreshSwatchPicker('#path-head-color');
      refreshSwatchPicker('#label-color');
      refreshSwatchPicker('#label-bg-color');

      this.queueRender(); // Highlight selection
    });

    // waypoint:multi-selected - Multiple waypoints selected via
    // shift/cmd-click (list), cmd-click (canvas), or Cmd/Ctrl+A.
    // The selection is normalised to route order so bulk operations and
    // the chip's counts are stable regardless of click order.
    this.eventBus.on('waypoint:multi-selected', ({ waypoints, primary }) => {
      const inSelection = new Set(waypoints);
      this.selectedWaypoints = this.waypoints.filter(wp => inSelection.has(wp));
      this.selectedWaypoint = primary;
      if (this.interactionHandler?.setSelection) {
        this.interactionHandler.setSelection(this.selectedWaypoints, primary);
      } else {
        this.interactionHandler?.setSelectedWaypoint?.(primary);
      }
      this.uiController?.setSelection(this.selectedWaypoints, primary);
      // Inspector compares each control's actual write targets and presents
      // disagreement explicitly; edits still write through one selection path.
      this.uiController?.updateWaypointEditor(primary, this.selectedWaypoints);
      this.updateWaypointList();
      this.updateWaypointEditor(); // Sync RoutePlotter's editor (camera controls etc.)
      this._updateCameraControlsVisibility(true); // Multi-select mode
      refreshSwatchPicker('#dot-color');
      refreshSwatchPicker('#segment-color');
      refreshSwatchPicker('#label-color');
      refreshSwatchPicker('#label-bg-color');
      this.queueRender();
    });

    // waypoint:toggle-select - Cmd/Ctrl+click on a waypoint (canvas):
    // grow or shrink the multi-selection one waypoint at a time
    this.eventBus.on('waypoint:toggle-select', (waypoint) => {
      const current = this.selectedWaypoints.length
        ? [...this.selectedWaypoints]
        : (this.selectedWaypoint ? [this.selectedWaypoint] : []);
      const index = current.indexOf(waypoint);

      if (index === -1) {
        const next = [...current, waypoint];
        if (next.length === 1) {
          this.eventBus.emit('waypoint:selected', waypoint);
        } else {
          this.eventBus.emit('waypoint:multi-selected', { waypoints: next, primary: waypoint });
        }
        return;
      }

      current.splice(index, 1);
      if (current.length === 0) {
        this.eventBus.emit('waypoint:deselected');
      } else if (current.length === 1) {
        this.eventBus.emit('waypoint:selected', current[0]);
      } else {
        const primary = this.selectedWaypoint === waypoint
          ? current[current.length - 1]
          : this.selectedWaypoint;
        this.eventBus.emit('waypoint:multi-selected', { waypoints: current, primary });
      }
    });

    // waypoint:deselected - All waypoints deselected
    this.eventBus.on('waypoint:deselected', () => {
      this.selectedWaypoint = null;
      this.selectedWaypoints = [];
      if (this.interactionHandler?.setSelection) {
        this.interactionHandler.setSelection([], null);
      } else {
        this.interactionHandler?.setSelectedWaypoint?.(null);
      }
      this.uiController?.setSelection([], null);
      this.uiController?.updateWaypointEditor(null);
      this.updateWaypointList();
      this.queueRender(); // Clear selection rings
    });
    
    // waypoint:delete - Request to delete a specific waypoint (from InteractionHandler)
    this.eventBus.on('waypoint:delete', (waypoint) => {
      this.deleteWaypoint(waypoint);
    });
    
    this.eventBus.on('waypoint:delete-selected', () => {
      // Multi-selection deletes as one gesture: every selected waypoint
      // goes in a single pass with a single undo entry (the per-waypoint
      // deleteWaypoint path would snapshot and recalc once per waypoint)
      if (this.selectedWaypoints.length > 1) {
        // Its message says the change: the chip and the list it moves are not said again (UI-06 PR 6)
        regionOperation(this, cover => {
          const doomed = [...this.selectedWaypoints];
          const firstIndex = this.waypoints.indexOf(doomed[0]);
          for (const wp of doomed) {
            const index = this.waypoints.indexOf(wp);
            if (index > -1) {
              this.waypoints.splice(index, 1);
              this._removeWaypointFromMap(wp);
            }
          }
          this.selectedWaypoint = null;
          this.selectedWaypoints = [];
          if (this.interactionHandler?.setSelection) {
            this.interactionHandler.setSelection([], null);
          } else {
            this.interactionHandler?.setSelectedWaypoint?.(null);
          }
          this.uiController?.setSelection([], null);
          // One pass through the shared deleted pipeline: one undo
          // snapshot, one path recalc, one autosave. The deselected event
          // then walks SectionController back to Route scope.
          this.eventBus.emit('waypoint:deleted', firstIndex);
          this.eventBus.emit('waypoint:deselected');
          this.announce(`${doomed.length} waypoints deleted`);
          cover();
        });
        return;
      }

      if (this.selectedWaypoint) {
        this.deleteWaypoint(this.selectedWaypoint);
        this.selectedWaypoint = null;
      }
    });
    
    this.eventBus.on('waypoints:clear-all', () => {
      this.clearAll();
    });
    
    // Project load remains a direct file-picker action. Save joins Undo/Redo
    // in setupDocumentCommands so toolbar and keyboard share one owner.
    this.elements.loadProjectBtn?.addEventListener('click', () => this.elements.loadProjectInput?.click());
    this.elements.loadProjectInput?.addEventListener('change', (e) => {
      const file = e.target.files?.[0];
      if (file) {
        this.loadProject(file);
        e.target.value = ''; // Reset input for re-selection
      }
    });
    
    // waypoint:add-at-center - Add new waypoint at center of visible canvas (AAA keyboard access)
    this.eventBus.on('waypoint:add-at-center', () => {
      if (!this.backgroundImage) return;
      
      // Calculate center of image in image coordinates
      const centerX = this.backgroundImage.width / 2;
      const centerY = this.backgroundImage.height / 2;
      
      // Create new waypoint at center
      const lastMajor = this.waypoints.filter(wp => wp.isMajor).pop();
      const newWaypoint = Waypoint.createMajor(centerX, centerY);
      if (lastMajor) {
        newWaypoint.copyPropertiesFrom(lastMajor);
      }
      
      this.waypoints.push(newWaypoint);
      this._addWaypointToMap(newWaypoint);
      
      // Select the new waypoint
      this.selectedWaypoint = newWaypoint;
      this.selectedWaypoints = [newWaypoint];
      this.uiController?.setSelection([newWaypoint], newWaypoint);
      if (this.interactionHandler?.setSelection) {
        this.interactionHandler.setSelection([newWaypoint], newWaypoint);
      } else {
        this.interactionHandler?.setSelectedWaypoint?.(newWaypoint);
      }
      
      // Update path and UI
      this.generatePathData();
      this.queueRender();
      this.uiController.updateWaypointList(this.waypoints);
      this.uiController.updateWaypointEditor(newWaypoint);
      this.autoSave();
      
      this.uiController.announce('Waypoint added at centre of map'); // the glossary's spelling
    });
    
    // waypoint:name-changed - Waypoint renamed in list
    this.eventBus.on('waypoint:name-changed', ({ waypoint, name }) => {
      // Name is already set on waypoint by the rename input
      this.saveUndoStateDebounced(); // Renames were not undoable before 2026-08-18
      this.autoSave();
    });
    
    // (waypoint:all-change is gone — the "All Waypoints" bulk mode
    // dissolved into ordinary multi-select. Cards write to every
    // selected waypoint through the normal single-change pipelines.)

    // ========== CANVAS CONTEXT MENU (review 2026-08-18) ==========
    // InteractionHandler has emitted these since v2 with no listener —
    // right-click was suppressed but dead. One ContextMenu instance
    // serves both menus.

    this.eventBus.on('waypoint:show-context-menu', ({ waypoint, x, y }) => {
      // Select first so the menu visibly acts on this waypoint — unless
      // it's already part of a multi-selection, which right-click keeps
      if (this.selectedWaypoint !== waypoint && !this.selectedWaypoints.includes(waypoint)) {
        this.eventBus.emit('waypoint:selected', waypoint);
      }

      const majorsCount = this.waypoints.filter(wp => wp.isMajor).length;
      const lastMajor = waypoint.isMajor && majorsCount <= 1;
      const items = [];

      if (waypoint.isMajor) {
        items.push({
          label: 'Rename',
          action: () => this.eventBus.emit('waypoint:request-rename', waypoint)
        });
      }
      items.push({
        label: waypoint.isMajor ? 'Convert to minor waypoint' : 'Convert to major waypoint',
        disabled: lastMajor,
        disabledReason: lastMajor ? 'The route needs at least one major waypoint' : undefined,
        action: () => this.eventBus.emit('waypoint:toggle-type', waypoint)
      });
      items.push({
        label: 'Insert waypoint before',
        action: () => this.eventBus.emit('waypoint:insert-adjacent', { waypoint, where: 'before' })
      });
      items.push({
        label: 'Insert waypoint after',
        action: () => this.eventBus.emit('waypoint:insert-adjacent', { waypoint, where: 'after' })
      });
      items.push({
        label: 'Delete waypoint',
        danger: true,
        separatorBefore: true,
        action: () => this.eventBus.emit('waypoint:delete', waypoint)
      });

      this._contextMenu ||= new ContextMenu();
      this._contextMenu.show({ x, y, items, ariaLabel: 'Waypoint actions' });
    });

    this.eventBus.on('canvas:show-context-menu', ({ x, y, canvasX, canvasY }) => {
      // Same bounds rule as click-to-add (coordinate:check-bounds above)
      const zoom = this.exportSettings.backgroundZoom / 100;
      let within;
      if (zoom < 1) {
        const canvas = this.screenToCanvas(canvasX, canvasY);
        within = this.coordinateTransform.isWithinCanvasBounds(canvas.x, canvas.y);
      } else {
        within = this.isWithinImageBounds(canvasX, canvasY);
      }
      if (!within || !this.background.image) return;

      const img = this.screenToImage(canvasX, canvasY);
      const noRoute = this.waypoints.length === 0;
      const items = [
        {
          label: 'Add waypoint here',
          action: () => this.eventBus.emit('waypoint:add', { imgX: img.x, imgY: img.y, isMajor: true })
        },
        {
          label: 'Add minor waypoint here',
          disabled: noRoute,
          disabledReason: noRoute ? 'Minor waypoints shape a leg — add a waypoint first' : undefined,
          action: () => this.eventBus.emit('waypoint:add', { imgX: img.x, imgY: img.y, isMajor: false })
        }
      ];

      this._contextMenu ||= new ContextMenu();
      this._contextMenu.show({ x, y, items, ariaLabel: 'Canvas actions' });
    });

    /**
     * waypoint:toggle-type - Convert major ↔ minor. Emitted by the T key
     * since v2 but never handled until the 2026-08-18 context menu work.
     * Type changes move pauses/labels/beacons in or out of the timeline,
     * so duration must be recalculated alongside the path.
     */
    this.eventBus.on('waypoint:toggle-type', (waypoint) => {
      if (!waypoint) return;
      const majorsCount = this.waypoints.filter(wp => wp.isMajor).length;
      if (waypoint.isMajor && majorsCount <= 1) {
        this.announce('Cannot convert the only major waypoint to minor');
        return;
      }

      waypoint.isMajor = !waypoint.isMajor;
      this._majorWaypointsCache = null;
      this.saveUndoState();

      if (this.waypoints.length >= 2) {
        this.calculatePath();
      }
      this.updateAnimationDuration();
      // Re-run the selection pipeline so editor, list, and canvas all
      // reflect the new type
      this.eventBus.emit('waypoint:selected', waypoint);
      this.autoSave();
      this.queueRender();
      this.announce(waypoint.isMajor ? 'Converted to major waypoint' : 'Converted to minor waypoint');
    });

    /**
     * waypoint:insert-adjacent - Insert a new major waypoint next to an
     * existing one (context menu). Positioned at the midpoint toward the
     * neighbour on that side, or extended past the endpoint when there
     * is none. waypoint:added handles undo/path/list/save.
     */
    this.eventBus.on('waypoint:insert-adjacent', ({ waypoint, where }) => {
      const index = this.waypoints.indexOf(waypoint);
      if (index === -1) return;

      const neighbour = this.waypoints[where === 'before' ? index - 1 : index + 1];
      let imgX, imgY;
      if (neighbour) {
        imgX = (waypoint.imgX + neighbour.imgX) / 2;
        imgY = (waypoint.imgY + neighbour.imgY) / 2;
      } else {
        // Endpoint: continue the route's direction by ~8% of the image,
        // clamped inside it; a single-waypoint route offsets rightward
        const ref = this.waypoints[where === 'before' ? index + 1 : index - 1];
        const dx = ref ? waypoint.imgX - ref.imgX : 1;
        const dy = ref ? waypoint.imgY - ref.imgY : 0;
        const len = Math.hypot(dx, dy) || 1;
        const step = 0.08;
        imgX = Math.min(1, Math.max(0, waypoint.imgX + (dx / len) * step));
        imgY = Math.min(1, Math.max(0, waypoint.imgY + (dy / len) * step));
      }

      const newWp = Waypoint.createMajor(imgX, imgY);
      newWp.copyPropertiesFrom(waypoint);
      this.waypoints.splice(where === 'before' ? index : index + 1, 0, newWp);

      this.eventBus.emit('waypoint:added', newWp);
      this.eventBus.emit('waypoint:selected', newWp);
      this.announce(where === 'before' ? 'Waypoint inserted before' : 'Waypoint inserted after');
    });

    /**
     * waypoint:request-rename - Focus the waypoint's list row and start
     * inline rename (context menu). Selection rebuilds the list, so the
     * rename call is deferred a frame to target the fresh row.
     */
    this.eventBus.on('waypoint:request-rename', (waypoint) => {
      if (this.selectedWaypoint !== waypoint) {
        this.eventBus.emit('waypoint:selected', waypoint);
      }
      requestAnimationFrame(() => this.uiController?.startRenameFor(waypoint));
    });

    // ========== VIDEO EXPORT EVENTS ==========
    
    /**
     * video:frame-rate-change - Update export frame rate setting
     */
    this.eventBus.on('video:frame-rate-change', (frameRate) => {
      this.exportSettings.frameRate = frameRate;
      console.debug(`🎬 [VideoExport] Frame rate set to ${frameRate} fps`);
    });
    
    
    /**
     * video:camera-change - Toggle whether preview/export applies per-waypoint camera zoom/pan.
     * Reflects live in Preview mode (WYSIWYG); Edit mode is unaffected. Persisted via autoSave.
     * emits from UIController checkbox → here → _calculateCameraState reads exportSettings.includeCamera
     */
    this.eventBus.on('video:camera-change', (includeCamera) => {
      this.exportSettings.includeCamera = includeCamera;
      this.autoSave();
      if (this.previewMode) this.queueRender(); // WYSIWYG: reflect toggle on the live canvas
    });
    
    /**
     * video:text-change - Toggle whether preview/export shows waypoint text labels.
     * Reflects live in Preview mode (WYSIWYG); Edit mode is unaffected. Persisted via autoSave.
     * emits from UIController checkbox → here → renderState.suppressLabels in render()
     */
    this.eventBus.on('video:text-change', (includeText) => {
      this.exportSettings.includeText = includeText;
      this.autoSave();
      if (this.previewMode) this.queueRender(); // WYSIWYG: reflect toggle on the live canvas
    });
    
    /**
     * video:resolution-change - Update export resolution and canvas aspect ratio
     * @param {Object} resolution - { width, height } - null values are ignored
     */
    this.eventBus.on('video:resolution-change', ({ width, height }) => {
      if (width !== null) {
        this.exportSettings.resolutionX = width;
      }
      if (height !== null) {
        this.exportSettings.resolutionY = height;
      }
      console.debug(`🎬 [VideoExport] Resolution set to ${this.exportSettings.resolutionX}×${this.exportSettings.resolutionY}`);
      
      // Update canvas aspect ratio to match export resolution
      this.updateCanvasAspectRatio();
    });
    
    /**
     * video:resolution-native - Set resolution to loaded image's native dimensions
     */
    this.eventBus.on('video:resolution-native', () => {
      if (this.background.image) {
        const width = this.background.image.naturalWidth;
        const height = this.background.image.naturalHeight;
        this.exportSettings.resolutionX = width;
        this.exportSettings.resolutionY = height;
        if (this.elements.exportResX) {
          this.elements.exportResX.value = width;
        }
        if (this.elements.exportResY) {
          this.elements.exportResY.value = height;
        }
        this.updateCanvasAspectRatio();
        console.debug(`📐 [Resolution] Set to native: ${width}×${height}`);
      } else {
        console.warn('📐 [Resolution] No image loaded for native resolution');
      }
    });
    
    /**
     * background:zoom-change - Update background zoom level
     * Zoom affects how the background is displayed and waypoint positions
     * @param {number} zoom - Zoom percentage (100 = no zoom, 200 = 2x zoom)
     */
    this.eventBus.on('background:zoom-change', (zoom) => {
      this.exportSettings.backgroundZoom = zoom;
      // Update coordinate transform with new zoom factor
      this.coordinateTransform.setBackgroundZoom(zoom / 100);
      if (getFinishContexts(this).finishPackingContext) {
        this.refreshSceneEnd();
        this._syncNodeAreaCard?.();
        this._queueSceneOutlineRefresh?.();
      }
      // Clamp waypoints that may now be out of bounds
      this.clampWaypointsToCanvas();
      this.render();
      this.autoSave();
      console.debug(`🔍 [Zoom] Background zoom set to ${zoom}%`);
    });
    
    /**
     * video:export-request - Start video export process
     * Uses frame-by-frame capture for consistent output
     * @param {string|{format: string, resolution?: {width: number, height: number}}} request -
     *   The format, or the format and the size to export at (the codec
     *   dialog's reduced MP4). While an export runs it is refused whole, the
     *   size included, which would resize the running export's canvas; the
     *   export sets both once it is the app's, so a request made from what
     *   setting them sets off is refused too (DEF-46).
     */
    this.eventBus.on('video:export-request', (request) => {
      const { format, resolution } = request && typeof request === 'object' ? request : { format: request };
      if (refuseWhileExporting(this)) return;
      if (!this.previewMode) {
        // Read politely, as a toast is (UI-06; the owner, 2026-10-09)
        this.showToast('Tip: Switch to Preview mode to see exactly how the export will look', 6000);
      }
      this.exportVideo({ format: format || 'mp4', resolution });
    });
    
    /**
     * html:export-request - Export as interactive HTML file
     * Creates a self-contained HTML file with embedded player
     */
    this.eventBus.on('html:export-request', () => {
      if (!this.previewMode) {
        this.showToast('Tip: Switch to Preview mode to see exactly how the export will look', 6000);
      }
      void this.requestHTMLExport();
    });
    
    // ========== MOTION VISIBILITY EVENTS ==========
    
    /**
     * motion:preview-mode-change - Toggle between edit and preview mode
     * Edit mode: everything visible for editing
     * Preview mode: applies motion visibility settings
     */
    this.eventBus.on('motion:preview-mode-change', (previewMode) => {
      this.previewMode = previewMode;
      console.debug(`👁️ [Motion] ${previewMode ? 'Preview' : 'Edit'} mode`);
      this.invalidateAnimationTiming();
      this.queueRender();
    });
    
    /**
     * motion:path-visibility-change - Update path visibility mode
     * Also toggles Trail Size control visibility (only shown for comet/instantaneous mode)
     */
    this.eventBus.on('motion:path-visibility-change', (mode) => {
      console.debug('[Motion] pathVisibility changed:', this.motionSettings.pathVisibility, '→', mode);
      this.motionSettings.pathVisibility = mode;
      this.uiController?.updateTrailControlVisibility?.(mode);

      this.invalidateAnimationTiming();
      this.autoSave();
      if (this.previewMode) this.queueRender();
    });
    
    /**
     * motion:path-trail-change - Update path trail fraction
     * @param {number} trailFraction - Trail as fraction of sequence (0=off, 0.01-1.0)
     */
    this.eventBus.on('motion:path-trail-change', (trailFraction) => {
      this.motionSettings.pathTrail = trailFraction;
      this.invalidateAnimationTiming();
      this.autoSave();
      if (this.previewMode) this.queueRender();
    });
    
    /**
     * motion:waypoint-visibility-change - Update waypoint visibility mode
     */
    this.eventBus.on('motion:waypoint-visibility-change', (mode) => {
      this.motionSettings.waypointVisibility = mode;
      // Hiding a waypoint before or after the head changes when its beacon
      // settles, and so where the scene ends (CROWD-05).
      this.refreshSceneEnd?.();
      this.autoSave();
      if (this.previewMode) this.render();
    });
    
    /**
     * motion:background-visibility-change - Update background visibility mode
     */
    this.eventBus.on('motion:background-visibility-change', (mode) => {
      this.motionSettings.backgroundVisibility = mode;
      // Reset reveal mask when switching to reveal modes
      if (mode === 'spotlight-reveal' || mode === 'angle-of-view-reveal') {
        this.motionVisibilityService.resetRevealMask();
      }
      this.invalidateAnimationTiming();
      this.autoSave();
      if (this.previewMode) this.queueRender();
    });
    
    /**
     * motion:reveal-size-change - Update reveal circle size
     */
    this.eventBus.on('motion:reveal-size-change', (sizePercent) => {
      this.motionSettings.revealSize = sizePercent;
      this.autoSave();
      if (this.previewMode) this.render();
    });
    
    /**
     * motion:reveal-feather-change - Update reveal feather width
     */
    this.eventBus.on('motion:reveal-feather-change', (featherPercent) => {
      this.motionSettings.revealFeather = featherPercent;
      this.autoSave();
      if (this.previewMode) this.render();
    });
    
    /**
     * motion:reveal-trail-change - REVEAL-01: how much of the path behind the
     * head stays revealed before the reveal fades out
     */
    this.eventBus.on('motion:reveal-trail-change', (trailPercent) => {
      this.motionSettings.revealTrail = trailPercent;
      this.autoSave();
      if (this.previewMode) this.render();
    });
    
    /**
     * motion:aov-angle-change - Update angle of view cone angle
     */
    this.eventBus.on('motion:aov-angle-change', (angleDegrees) => {
      this.motionSettings.aovAngle = angleDegrees;
      this.autoSave();
      if (this.previewMode) this.render();
    });
    
    /**
     * motion:aov-distance-change - Update angle of view distance
     */
    this.eventBus.on('motion:aov-distance-change', (distancePercent) => {
      this.motionSettings.aovDistance = distancePercent;
      this.autoSave();
      if (this.previewMode) this.render();
    });
    
    /**
     * motion:aov-dropoff-change - Update angle of view dropoff
     */
    this.eventBus.on('motion:aov-dropoff-change', (dropoffPercent) => {
      this.motionSettings.aovDropoff = dropoffPercent;
      this.autoSave();
      if (this.previewMode) this.render();
    });
    
    // Waypoint reordering from UIController drag-and-drop
    this.eventBus.on('waypoints:reordered', (newOrder) => {
      // Each major moves as a block with its trailing minors. Rebuilding
      // majors in place while minors kept their array slots silently
      // reattached minors to different legs (data bug, review 2026-08-18).
      this.waypoints = reorderWaypointBlocks(this.waypoints, newOrder);

      // Cached major positions are index-derived — stale after reorder
      this._majorWaypointsCache = null;
      this.saveUndoState();

      // Recalculate path and update
      if (this.waypoints.length >= 2) {
        this.calculatePath();
      }
      this.updateWaypointList();
      this.autoSave();
      this.queueRender();
    });
    
    // Coordinate conversion callbacks
    // All mouse event coordinates are in SCREEN space (CSS pixels relative to canvas element)
    // These events handle the full pipeline: Screen ↔ Canvas ↔ Image
    
    this.eventBus.on('coordinate:canvas-to-image', (data, callback) => {
      // Input: screen coords (despite legacy name "canvasX/Y")
      // Output: normalized image coords (0-1)
      const result = this.screenToImage(data.canvasX, data.canvasY);
      if (callback) callback(result);
    });
    
    this.eventBus.on('coordinate:image-to-canvas', (data, callback) => {
      // Input: normalized image coords (0-1)
      // Output: screen coords (for comparison with mouse events)
      // Note: Returns SCREEN coords so drag offsets work correctly when zoomed
      const result = this.imageToScreen(data.imgX, data.imgY);
      if (callback) callback(result);
    });
    
    this.eventBus.on('coordinate:check-bounds', (data, callback) => {
      // Input: screen coords
      // Output: boolean (is click within allowable area)
      const zoom = this.exportSettings.backgroundZoom / 100;
      let isWithin;
      if (zoom < 1) {
        // Zoomed out: allow waypoints anywhere on the canvas surface
        const canvas = this.screenToCanvas(data.canvasX, data.canvasY);
        isWithin = this.coordinateTransform.isWithinCanvasBounds(canvas.x, canvas.y);
      } else {
        // Zoomed in or 100%: restrict to image bounds
        isWithin = this.isWithinImageBounds(data.canvasX, data.canvasY);
      }
      if (callback) callback(isWithin);
    });
    
    this.eventBus.on('waypoint:check-branch-handle', ({ x, y }, callback) => {
      if (callback) callback(this.findBranchHandleAt(x, y, boundEntryWaypointIds(this.scene)));
    });

    this.eventBus.on('waypoint:check-at-position', (pos, callback) => {
      const waypoint = this.findWaypointAt(pos.x, pos.y);
      if (callback) callback(waypoint);
    });
    
    // Area highlight handle hit test (for edit dragging)
    this.eventBus.on('area:check-handle', ({ screenX, screenY }, callback) => {
      if (callback) callback(findAreaHandleAtScreen(this, screenX, screenY));
    });

    // ========== CANVAS HOVER AFFORDANCES (Phase 4) ==========

    /**
     * canvas:hover-move - Idle pointer moved (rAF-throttled by
     * InteractionHandler). Cascades hit-tests in the same priority order
     * as mousedown — area handle, waypoint, leg midpoint "+", leg — and
     * stores the result on this.canvasHover for the hover render layers.
     * Calls back with the hover kind so the cursor can react.
     */
    this.eventBus.on('canvas:hover-move', ({ x, y }, callback) => {
      let hover = null;

      if (!this.previewMode && !this.areaDrawingService?.isDrawing) {
        // Area handles first (they sit above waypoints when editing)
        const areaHandle = findAreaHandleAtScreen(this, x, y);
        if (areaHandle) {
          const { waypoint, imageToScreen: _imageToScreen, ...handle } = areaHandle;
          hover = { type: 'area-handle', waypoint, handle };
        }

        if (!hover) {
          // The branch handle beside a waypoint a bound crowd enters from
          // (COMPOSE-04): the hero peels off exactly where the crowd joins.
          // Checked ahead of the waypoint because it deliberately sits clear
          // of the marker, and so outside the marker's own hit radius.
          const forkable = this.findBranchHandleAt(x, y, boundEntryWaypointIds(this.scene));
          if (forkable) hover = { type: 'waypoint-plus', waypoint: forkable };
        }

        if (!hover) {
          const waypoint = this.findWaypointAt(x, y);
          if (waypoint) {
            hover = { type: 'waypoint', waypoint };
          }
        }

        if (!hover) {
          const segmentHit = this.findSegmentAt(x, y);
          if (segmentHit) {
            hover = {
              type: segmentHit.onPlus ? 'leg-plus' : 'leg',
              waypoint: segmentHit.waypoint,
              waypointIndex: segmentHit.waypointIndex,
              // The leg's run, which the glow is drawn along, and whether it
              // offers the "+" (DEF-64)
              branchId: segmentHit.branchId,
              canInsert: segmentHit.canInsert
            };
          }
        }
      }

      const prev = this.canvasHover;
      // A fork owns its trunk leg and each branch's first leg, so the run
      // tells those legs apart (DEF-64)
      const changed = (prev?.type !== hover?.type) ||
                      (prev?.waypoint !== hover?.waypoint) ||
                      (prev?.waypointIndex !== hover?.waypointIndex) ||
                      (prev?.branchId !== hover?.branchId) ||
                      (prev?.handle?.type !== hover?.handle?.type) ||
                      (prev?.handle?.vertexIndex !== hover?.handle?.vertexIndex);
      if (changed) {
        this.canvasHover = hover;
        this.queueRender();
      }
      if (callback) callback(hover?.type || null);
    });

    /**
     * canvas:hover-clear - Pointer left the canvas or a drag started.
     */
    this.eventBus.on('canvas:hover-clear', () => {
      if (this.canvasHover) {
        this.canvasHover = null;
        this.queueRender();
      }
    });

    /**
     * segment:check-at-position - Leg hit test for clicks (edit mode only;
     * findSegmentAt guards preview itself).
     */
    this.eventBus.on('segment:check-at-position', (pos, callback) => {
      if (callback) callback(this.findSegmentAt(pos.x, pos.y));
    });

    /**
     * segment:clicked - A leg was clicked: select the waypoint that owns
     * it and flash the inspector's Leg card, teaching the ownership rule
     * the card header names ("Leg → Waypoint 3").
     */
    this.eventBus.on('segment:clicked', ({ waypoint }) => {
      if (!waypoint) return;
      this.eventBus.emit('waypoint:selected', waypoint);
      this.eventBus.emit('section:flash', { section: 'leg' });
    });

    /**
     * waypoint:insert-on-leg - The leg midpoint "+" handle: insert a
     * minor waypoint exactly between the leg's endpoints (array index
     * owner+1), positioned on the path midpoint, so the leg gains a
     * shaping point without visually moving. waypoint:added handles
     * undo/path/list/save; the fresh minor becomes the selection (same
     * rule as insert-adjacent).
     */
    this.eventBus.on('waypoint:insert-on-leg', ({ waypointIndex, imgX, imgY }) => {
      const owner = this.waypoints[waypointIndex];
      if (!owner || waypointIndex >= this.waypoints.length - 1) return;

      // The path midpoint can overshoot its endpoints on a curved leg (DEF-03)
      const newWp = Waypoint.createMinor(clampImageCoordinate(imgX), clampImageCoordinate(imgY));
      newWp.copyPropertiesFrom(owner);
      this.waypoints.splice(waypointIndex + 1, 0, newWp);

      // Leg geometry changed under the pointer; next mousemove re-tests
      this.canvasHover = null;

      // Its message says the change: the chip's "Editing · minor waypoint"
      // after it would add no number or count (Codex r2 on 753f93f, Q1)
      regionOperation(this, cover => {
        this.eventBus.emit('waypoint:added', newWp);
        this.eventBus.emit('waypoint:selected', newWp);
        this.announce('Minor waypoint added to leg');
        cover();
      });
    });

    // Help events: Help is a dialog of its own, its shortcuts grid open; the
    // splash is the first-run welcome (UI-06 J-01)
    this.eventBus.on('help:toggle', () => {
      this.toggleHelp();
    });
    
    // `?` (InteractionHandler)
    this.eventBus.on('help:show-shortcuts', () => {
      this.showHelp();
    });
    
    // ========== WAYPOINT KEYBOARD EVENTS ==========
    
    this.eventBus.on('waypoint:add-at-center', () => {
      // Add waypoint at canvas center (or end of path if waypoints exist)
      const centerX = 0.5;
      const centerY = 0.5;
      this.eventBus.emit('waypoint:add', {
        imgX: centerX,
        imgY: centerY,
        isMajor: true
      });
      this.announce('Waypoint added at centre'); // the glossary's spelling
    });
    
    this.eventBus.on('waypoint:deselect', () => {
      // `this.selectWaypoint(null)` here threw since the mixin split (no
      // such method survives on the prototype; the bus swallowed the
      // error), so Escape never cleared waypoint selection through this
      // handler. Route through the canonical deselected pipeline.
      if (this.selectedWaypoint) {
        regionOperation(this, cover => {
          this.eventBus.emit('waypoint:deselected');
          this.announce('Selection cleared');
          cover();
        });
      }
    });
    
    this.eventBus.on('waypoint:duplicate', () => {
      if (!this.selectedWaypoint) {
        this.announce('No waypoint selected');
        return;
      }
      // Create duplicate offset slightly from original
      const offset = 0.02; // 2% offset
      const newX = Math.min(1, this.selectedWaypoint.imgX + offset);
      const newY = Math.min(1, this.selectedWaypoint.imgY + offset);
      
      this.eventBus.emit('waypoint:add', {
        imgX: newX,
        imgY: newY,
        isMajor: this.selectedWaypoint.isMajor
      });
      this.announce('Waypoint duplicated');
    });
    
    /**
     * waypoint:select-all - Cmd/Ctrl+A selects the whole route as an
     * ordinary multi-select, minors included. (The old handler called a
     * UIController method that never existed — the bus swallowed the
     * TypeError, so Cmd+A was dead until the Phase 4 multi-select work.)
     */
    this.eventBus.on('waypoint:select-all', () => {
      if (this.waypoints.length === 0) return;
      if (this.waypoints.length === 1) {
        this.eventBus.emit('waypoint:selected', this.waypoints[0]);
      } else {
        // Keep the current primary when it's part of the route
        const primary = this.selectedWaypoint && this.waypoints.includes(this.selectedWaypoint)
          ? this.selectedWaypoint
          : this.waypoints[0];
        this.eventBus.emit('waypoint:multi-selected', {
          waypoints: [...this.waypoints],
          primary
        });
      }
      this.announce('All waypoints selected');
    });

    // Generic toast request from components (e.g. shift-delete undo hint);
    // `priority` is the announcement's (assertive for an error, UI-06 J-05);
    // `essential`, one routine messages never displace (DEF-98)
    this.eventBus.on('ui:toast', ({ message, duration, priority, essential = false }) => {
      this.showToast(message, duration, null, essential ? { priority, essential } : { priority });
    });

    // Announcement request from components (UIController's list reorders):
    // queued with the app's own, so it waits its turn too (DEF-45)
    // `unlessSaid`: what a region that is no longer live said alone, said
    // once unless the change's own message says it (UI-06 PR 6, item 6);
    // `region`, whose line it is: a later one of the same turn replaces it;
    // `heard`, focus names the change already: it cancels the region's line
    // still waiting, and says none (Codex r2 on 753f93f, 4).
    this.eventBus.on('ui:announce', ({ message, unlessSaid = false, region, heard = false }) => {
      if (unlessSaid) this.announceUnlessSaid(message, region, { heard });
      else this.announce(message);
    });
    
    // Path head (route-global) is wired directly in setupEventListeners;
    // the old pathhead:* event pair duplicated every mutation, undo
    // snapshot, and autosave, and forced a second synchronous render
    // per input event (removed 2026-08-18).

    // ========== ZOOM/PAN EVENTS ==========
    
    this.eventBus.on('canvas:zoom-in', () => {
      this.zoomIn();
    });
    
    this.eventBus.on('canvas:zoom-out', () => {
      this.zoomOut();
    });
    
    this.eventBus.on('canvas:zoom-reset', () => {
      this.resetZoom();
    });
    
    // ========== UNDO/REDO EVENTS ==========
    
    this.eventBus.on('undo:state-change', ({ canUndo, canRedo }) => {
      if (this.elements.undoBtn) {
        this.elements.undoBtn.disabled = !canUndo;
      }
      if (this.elements.redoBtn) {
        this.elements.redoBtn.disabled = !canRedo;
      }
    });
    
    // Document commands have one EventBus-owned execution path. Both toolbar
    // buttons and InteractionHandler shortcuts emit these events exactly once.
    setupDocumentCommands(this);
  },

  /**
   * End the armed branch gesture: disarm it and take its banner away. Focus
   * held in the banner (its Cancel pressed) goes back to the element focused
   * when it was armed, else to the fork's row, looked up afresh (a rebuild
   * replaces rows), else to the Route button, each through `focusLands`
   * (proposal (c), accepted by the owner; UI-06 PR 6). Where the model
   * changed under the gesture, focus is placed once the list the change
   * rebuilds has drawn, after the frames it queued (Codex's recheck).
   * @param {{deferFocus?: boolean}} [options]
   * @private
   */
  _endBranchGesture({ deferFocus = false } = {}) {
    const gesture = this._branchGesture;
    this._branchGesture = null;
    if (this.interactionHandler) this.interactionHandler.branchArmed = null;
    if (!gesture) return;
    const heldFocus = this.modeBanner?.holdsFocus(gesture.owner) ?? false;
    this.modeBanner?.hide(gesture.owner);
    if (!heldFocus) return;
    const place = () => {
      const fork = this.waypointsById?.get(gesture.forkId);
      returnFocus(gesture.focusAtArm, fork ? this.uiController?.rowButtonFor?.(fork) : null);
    };
    if (!deferFocus) {
      place();
      return;
    }
    const token = {};
    this._branchFocusToken = token;
    Promise.resolve().then(() => {
      if (this._branchFocusToken !== token) return;
      this._branchFocusFrame = requestAnimationFrame(() => {
        this._branchFocusFrame = null;
        if (this._branchFocusToken !== token) return;
        this._branchFocusToken = null;
        const active = document.activeElement;
        // Unless the author has put focus somewhere since.
        if (!active || active === document.body || active === document.documentElement || !active.isConnected) place();
      });
    });
  },

  /**
   * An armed branch gesture follows the model as an area draw does: it is
   * cancelled once its fork is no longer the model's own waypoint — deleted,
   * or replaced by an Undo, a Redo, an Open or Clear All, which put copies
   * back: it is never re-pointed by id, so a placement never copies from a
   * stale waypoint (UI-06 PR 6, the lead's settlements 2 and 6).
   */
  followBranchFork() {
    const fork = this.interactionHandler?.branchArmed;
    if (!fork || this.waypointsById?.get(fork.id) === fork) return;
    this.eventBus.emit('route:branch-cancel', { deferFocus: true });
  },

  /** Stop a branch's focus step still waiting for its frame (teardown). @private */
  _stopBranchFocus() {
    this._branchFocusToken = null;
    if (this._branchFocusFrame) cancelAnimationFrame(this._branchFocusFrame);
    this._branchFocusFrame = null;
  }
};
